// THE COMMAND LIST — the ONE list of app actions behind the shell's command
// dock (App.jsx `commands=`), Ink's pattern (ink/src/lib/commands.js): the
// dock is the ecosystem's COMMAND CENTER — a verb-grammar palette + the
// session's flight recorder (every executed line and host-logged action
// streams into its transcript, dockLog.js). Synapse files its commands under
// the shell's canonical headings (DOCK_GROUPS) and injects its live
// conversations as quick-switch items, exactly as Ink injects notes.
import { DOCK_GROUPS, PANEL_HOTKEYS, formatHotkey } from "@ofself/shell";

// No app-level hotkey map any more: the PANEL bindings are shell chrome
// since v0.94.0 (PANEL_HOTKEYS — the Shell dispatches them app-wide for any
// rail item declaring `panel`, the same law as the dock's ⌘J). The panel
// rows below carry them as display-only `shortcut`s.

// …except FULL SCREEN, which is an app action: declared on its command row
// below (`hotkey:`), so the shell binds it app-wide AND renders it in the
// dock — one pipeline (hotkeys.js), no second registry. mod+shift+f is
// Ink's focus binding: one ecosystem muscle memory for "the work, full
// screen", not a per-app invention.
export const SYNAPSE_HOTKEYS = {
  focus: "mod+shift+f",
};

// Page nav mirrored from App's PAGE_NAV ids — labels only (the dock renders
// no icons for navigate rows; the sidebar header owns the glyphs).
const PAGES = [
  { id: "home", label: "Messages" },
  { id: "activity", label: "Activity" },
  { id: "files", label: "Files" },
  { id: "huddles", label: "Huddles" },
];

/**
 * Build the dock's command list.
 * ctx:
 *   goTo(pageId)        — switch app page
 *   composeDM()         — start a new direct message
 *   composeChannel()    — start a new channel (omit when no active group)
 *   toggleSidebar()     — left panel toggle
 *   openSettings()      — the shell settings window
 *   openSearch()        — the universal spotlight
 *   conversations       — [{ id, label, open() }] live channels + DMs
 */
export function buildSynapseCommands(ctx) {
  const conversations = ctx.conversations || [];
  const findConversation = (name) => {
    const lower = name.toLowerCase();
    return (
      conversations.find((c) => c.label.toLowerCase() === lower) ||
      conversations.find((c) => c.label.toLowerCase().startsWith(lower)) ||
      conversations.find((c) => c.label.toLowerCase().includes(lower))
    );
  };

  return [
    // ── Commands (the default heading) ──────────────────────────────────
    {
      id: "new-dm",
      label: "New direct message",
      keywords: ["compose", "dm", "message", "chat", "write"],
      run: ctx.composeDM,
    },
    ...(ctx.composeChannel
      ? [
          {
            id: "new-channel",
            label: "New channel",
            keywords: ["create", "room", "compose"],
            run: ctx.composeChannel,
          },
        ]
      : []),
    {
      id: "search",
      label: "Search",
      keywords: ["find", "spotlight", "messages"],
      run: ctx.openSearch,
    },
    {
      id: "settings",
      label: "Open settings",
      keywords: ["preferences", "theme", "appearance"],
      run: ctx.openSettings,
    },
    // ── Navigate ────────────────────────────────────────────────────────
    {
      id: "panel-left",
      label: "Toggle left panel",
      group: DOCK_GROUPS.NAVIGATE,
      keywords: ["sidebar", "collapse"],
      shortcut: formatHotkey(PANEL_HOTKEYS.left),
      run: ctx.toggleSidebar,
    },
    ...(ctx.toggleFocus
      ? [
          {
            id: "focus",
            label: ctx.focusMode ? "Exit full screen" : "Full screen",
            group: DOCK_GROUPS.NAVIGATE,
            keywords: ["fullscreen", "focus", "zen", "expand"],
            hotkey: SYNAPSE_HOTKEYS.focus,
            run: ctx.toggleFocus,
          },
        ]
      : []),
    ...PAGES.map((p) => ({
      id: `go-${p.id}`,
      label: `Go to ${p.label}`,
      group: DOCK_GROUPS.NAVIGATE,
      keywords: ["page", "view", p.label.toLowerCase()],
      run: () => ctx.goTo(p.id),
    })),
    // ── The CLI verb — "open <channel | person>" with live completion ───
    {
      id: "cli-open",
      label: "Open conversation",
      verbs: ["open"],
      args: [
        {
          name: "name",
          hint: "channel | person",
          rest: true,
          complete: (prefix) => {
            const p = (prefix || "").toLowerCase();
            return conversations
              .map((c) => c.label)
              .filter((l) => l.toLowerCase().startsWith(p));
          },
        },
      ],
      template: ({ name }) => `open ${name || ""}`,
      runArgs: ({ name }) => {
        if (!name) return "usage: open <channel | person>";
        const match = findConversation(name);
        if (!match) return `no conversation named "${name}"`;
        match.open();
        return `opened ${match.label}`;
      },
    },
    // ── Quick-switch: every live conversation, fuzzy-reachable ──────────
    ...conversations.map((c) => ({
      id: `conv-${c.id}`,
      label: c.label,
      group: "Conversations",
      keywords: ["open", "conversation"],
      run: c.open,
    })),
  ];
}
